import posthog, { DisplaySurveyType } from 'posthog-js'
import type { SessionRecordingOptions } from 'posthog-js'
import { FiltradorDeErros } from 'src/erros'
import { obterSecret } from 'src/typescript/servicos/bifrost/bifrost.servico'
import {
  criarFluxoSurveyBannerGestoes,
  type ClienteSurveyPosthog,
  type PropriedadesCliqueBannerGestoes,
} from './survey-banner-gestoes'

let posthogInicializado = false
const ouvintesInicializacaoPosthog = new Set<() => void>()
const ATRIBUTO_POSTHOG_EXIBIR_PONTO_NA_GRAVACAO = 'data-posthog-exibir-ponto-na-gravacao'
const clienteSurveyPosthog: ClienteSurveyPosthog = {
  onSurveysLoaded: (callback) => posthog.onSurveysLoaded(
    (surveys, contexto) => callback(surveys, contexto),
  ),
  canRenderSurvey: (idSurvey) => posthog.canRenderSurvey(idSurvey),
  displaySurvey: (idSurvey, opcoes) => {
    posthog.displaySurvey(idSurvey, {
      ...opcoes,
      displayType: DisplaySurveyType.Popover,
    })
  },
  on: (evento, callback) => posthog.on(evento, callback),
}
const fluxoSurveyBannerGestoes = criarFluxoSurveyBannerGestoes(
  clienteSurveyPosthog,
  () => verificarPosthogEstaAtivo(),
  (callback) => registrarOuvinteInicializacaoPosthog(callback),
)
const mascararInputPosthog: NonNullable<SessionRecordingOptions['maskInputFn']> = (texto: string, elemento?: HTMLElement) => {
  if (elemento?.getAttribute(ATRIBUTO_POSTHOG_EXIBIR_PONTO_NA_GRAVACAO) === 'true') {
    return texto
  }

  return '*'.repeat(texto.length)
}

export interface UsuarioPosthog {
  idUsuario: string
  emailUsuario: string
  nomeUsuario: string
  papelUsuario:string
  idEmpresaUsuario:string
  nomeEmpresaUsuario:string
  tamanhoEmpresaUsuario: string;
  canal: string;
}
function ehAmbienteProducao (): boolean {
  return process.env.NODE_ENV === 'production'
}

function registrarOuvinteInicializacaoPosthog (callback: () => void): () => void {
  if (verificarPosthogEstaAtivo()) {
    callback()
    return () => {}
  }

  ouvintesInicializacaoPosthog.add(callback)
  return () => ouvintesInicializacaoPosthog.delete(callback)
}

function notificarInicializacaoPosthog () {
  const ouvintes = [...ouvintesInicializacaoPosthog]
  ouvintesInicializacaoPosthog.clear()

  for (const ouvinte of ouvintes) {
    try {
      ouvinte()
    } catch (erro) {
      FiltradorDeErros.capturarErro(erro as Error)
    }
  }
}

export function verificarSeUsuarioPermitido (emailUsuario: string): boolean {
  const usuarioEhLar21 = emailUsuario.includes('lar21')
  const usuararioEhPontotel = emailUsuario.includes('@pontotel')
  const usuararioEhPtt = emailUsuario.includes('@ptt')
  if (usuarioEhLar21 || usuararioEhPontotel || usuararioEhPtt) return false
  return true
}

export async function inicializarPosthog () {
  try {
    if (posthogInicializado) return
    if (ehAmbienteProducao()) {
      posthog.init(obterSecret('POSTHOG_API_KEY'), {
        api_host: process.env.VUE_APP_POSTHOG_API_HOST || 'https://us.i.posthog.com',
        autocapture: true,
        capture_pageview: false,
        capture_pageleave: false,
        person_profiles: 'identified_only',
        session_recording: {
          maskAllInputs: true,
          maskInputFn: mascararInputPosthog,
        },
      })
      posthogInicializado = true
      notificarInicializacaoPosthog()
    }
  } catch (erro) {
    FiltradorDeErros.capturarErro(erro as Error)
  }
}

export async function identificarUsuarioPosthog (usuario: UsuarioPosthog) {
  try {
    if (!verificarPosthogEstaAtivo()) {
      return
    }

    // Se já identificamos nesta sessão do navegador, não faz nada
    if (sessionStorage.getItem(`ph_id_${usuario.idUsuario}`)) {
      return
    }

    // Propriedades que nunca mudam - usa $set_once (só define na primeira vez no PostHog)
    const propriedadesImutaveis = {
        idUsuario: usuario.idUsuario,
        idEmpresaUsuario: usuario.idEmpresaUsuario,
        nomeEmpresaUsuario: usuario.nomeEmpresaUsuario
    }

    // Propriedades que podem mudar - agrupadas em um único $set
    const propriedadesMutaveis = {
        email: usuario.emailUsuario,
        nomeUsuario: usuario.nomeUsuario,
        papelUsuario: usuario.papelUsuario,
        tamanhoEmpresaUsuario: usuario.tamanhoEmpresaUsuario,
        canal: usuario.canal
    }

    // Combina as propriedades
    posthog.identify(
      usuario.idUsuario,
      {
        ...propriedadesMutaveis
      },
      {
        ...propriedadesImutaveis
      }
    )

    // Marca como identificado para não repetir até o usuário fechar a aba
    sessionStorage.setItem(`ph_id_${usuario.idUsuario}`, 'true')
  } catch (erro) {
    FiltradorDeErros.capturarErro(erro as Error)
  }
}

export async function enviarEventoPosthog (name: string, details?: Record<string, string | number>) {
  try {
    if (verificarPosthogEstaAtivo()) {
      posthog.capture(name, details)
    }
  } catch (erro) {
    FiltradorDeErros.capturarErro(erro as Error)
  }
}

export function prepararSurveyBannerGestoesPosthog (): () => void {
  try {
    return fluxoSurveyBannerGestoes.preparar()
  } catch (erro) {
    FiltradorDeErros.capturarErro(erro as Error)
    return () => {}
  }
}

export function exibirSurveyBannerGestoesPosthog (
  propriedades: PropriedadesCliqueBannerGestoes,
  aoFinalizarSurvey: () => void,
): boolean {
  try {
    return fluxoSurveyBannerGestoes.exibirSurvey(propriedades, aoFinalizarSurvey)
  } catch (erro) {
    fluxoSurveyBannerGestoes.cancelarInteracao()
    FiltradorDeErros.capturarErro(erro as Error)
    return false
  }
}

export function cancelarSurveyBannerGestoesPosthog () {
  fluxoSurveyBannerGestoes.cancelarInteracao()
}

export function verificarPosthogEstaAtivo (): boolean {
  return posthogInicializado
}

export async function resetarUsuarioPosthog (device: boolean = false) {
  try {
    posthog.reset(device)
    sessionStorage.clear()
    posthogInicializado = false
  } catch (erro) {
    FiltradorDeErros.capturarErro(erro as Error)
  }
}
